// Pure helpers for pies, numbers and cross-referencing. No React here.

const PALETTE = ['#8A63E0', '#E0A96D', '#7FB59A', '#B9A2F0', '#D98BA0', '#6C8EBF', '#C9B8F2', '#3E2380'];
const ACCENT = '#5B34B8';
const CASH = '#D9D4E3';
const UNALLOCATED = '#ECE8F4';

export function toNumber(value) {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.\-]/g, ''));
  return Number.isNaN(n) ? 0 : n;
}

export function isCash(h) {
  return h.assetKey === 'Cash' || /^\s*cash\s*$/i.test(h.name || '');
}

/** First holding gets the accent, cash is always grey, the rest cycle the palette. */
export function holdingColor(h, index) {
  if (isCash(h)) return CASH;
  if (index === 0) return ACCENT;
  return PALETTE[(index - 1) % PALETTE.length];
}

/**
 * Group the same underlying fund across tickers, so VOO and VUAG count as one.
 * Store the result on each holding when saving (holding.assetKey).
 */
export function deriveAssetKey(h) {
  const t = String(h.ticker || '').toUpperCase().trim();
  const n = String(h.name || '').toLowerCase();
  if (/^cash$/.test(n.trim())) return 'Cash';
  if (['VOO', 'VUAG', 'VUSA', 'CSPX', 'SPY', 'IVV'].includes(t) || n.includes('s&p 500')) return 'S&P 500';
  if (['VWRP', 'VWRL'].includes(t) || n.includes('all-world')) return 'FTSE All-World';
  if (t === 'SGLN' || n.includes('gold')) return 'Gold';
  if (n.includes('gilt')) return 'UK gilts';
  return t || h.name || 'Other';
}

export function assetKeyOf(h) {
  return h.assetKey || deriveAssetKey(h);
}

/** CSS conic-gradient for a donut. Unallocated space shows as pale grey. */
export function donutGradient(holdings) {
  let acc = 0;
  const parts = [];
  holdings.forEach((h, i) => {
    const pct = Math.max(0, toNumber(h.pct));
    if (!pct || acc >= 100) return;
    const to = Math.min(acc + pct, 100);
    parts.push(`${holdingColor(h, i)} ${acc}% ${to}%`);
    acc = to;
  });
  if (acc < 100) parts.push(`${UNALLOCATED} ${acc}% 100%`);
  return `conic-gradient(${parts.join(', ')})`;
}

/** Screen-reader text for a pie. */
export function pieLabel(ownerName, holdings) {
  const bits = holdings
    .filter((h) => toNumber(h.pct) > 0)
    .map((h) => `${h.ticker || h.name} ${toNumber(h.pct)}%`);
  return `${ownerName} portfolio: ${bits.length ? bits.join(', ') : 'no holdings yet'}`;
}

export function totalPct(holdings) {
  const sum = holdings.reduce((s, h) => s + Math.max(0, toNumber(h.pct)), 0);
  return Math.round(sum * 10) / 10;
}

export const formatPct = (n) => `${Math.round(toNumber(n) * 10) / 10}%`;
export const formatReturn = (n) => `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`;
export const returnClass = (n) => (n >= 0 ? 'pos' : 'neg');
export const formatMoney = (n) => `£${Math.round(n).toLocaleString('en-GB')}`;

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();            // Firestore Timestamp
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
}

export function firstName(user, currentUid) {
  if (user.uid === currentUid) return 'you';
  return user.name.split(' ')[0];
}

/** Names of other members who hold the same asset. */
export function holdersOf(users, assetKey, exceptUid, currentUid) {
  if (assetKey === 'Cash') return [];
  return users
    .filter((u) => u.uid !== exceptUid)
    .filter((u) => (u.portfolio?.holdings || []).some((h) => assetKeyOf(h) === assetKey))
    .map((u) => firstName(u, currentUid));
}

export function alsoHeldText(names) {
  if (!names.length) return '';
  if (names.length === 1) return `Also held by ${names[0]}`;
  if (names.length === 2) return `Also held by ${names[0]} and ${names[1]}`;
  return `Also held by ${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

/** The assets that appear in the most members' pies (for "Held by several of us"). */
export function mostHeld(users, currentUid, limit = 4) {
  const map = new Map();
  users.forEach((u) => {
    (u.portfolio?.holdings || []).forEach((h) => {
      const key = assetKeyOf(h);
      if (key === 'Cash') return;
      const entry = map.get(key) || { key, tickers: new Set(), names: [] };
      if (h.ticker) entry.tickers.add(h.ticker);
      entry.names.push(firstName(u, currentUid));
      map.set(key, entry);
    });
  });
  return [...map.values()]
    .sort((a, b) => b.names.length - a.names.length || a.key.localeCompare(b.key))
    .slice(0, limit)
    .map((e) => ({
      key: e.key,
      tickers: [...e.tickers].join(' · ') || '—',
      count: e.names.length,
      names: e.names.join(', '),
      share: Math.round((e.names.length / users.length) * 100),
    }));
}

export function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return 'Morning';
  if (h < 18) return 'Afternoon';
  return 'Evening';
}
